/**
 * Convert plain emoji-bearing text into pixel-art icons. Centralised here so
 * BOTH `login.ts`'s shared `el()` helper AND game-specific code can produce
 * the same crisp inline icons without spreading the mapping around.
 *
 * Each emoji is matched and swapped for an `<img>` at ~1.15em (or 1.3em
 * inside headings) so it tracks the surrounding text size and stays in
 * NearestNeighbor-pixel-art proportions.
 */

const EMOJI_SPRITES: Record<string, string> = {
  '🐾': '/sprites/iconchat_neutre.png',
  '🌳': '/sprites/iconchat_crier.png',
  '⚔️': '/sprites/arme_epee.png',
  '⚔': '/sprites/arme_epee.png',
  '🛡️': '/sprites/arme_bouclier_pierre.png',
  '🛡': '/sprites/arme_bouclier_pierre.png',
  '🏰': '/sprites/chateau_tier1.png',
  '🏛️': '/sprites/chateau_tier3.png',
  '🏛': '/sprites/chateau_tier3.png',
  '🪵': '/sprites/ressource_bois.png',
  '🪓': '/sprites/arme_hache_pierre.png',
  '🧱': '/sprites/ressource_pierre.png',
  '💰': '/sprites/piece_or.png',
  '👑': '/sprites/crown.png',
  '⛪': '/sprites/iconchat_valide.png',
  '🏹': '/sprites/arme_arc_bois.png',
  '🎯': '/sprites/arme_warshooter_bois.png',
  '😴': '/sprites/iconchat_perdu.png',
  '🔒': '/sprites/iconchat_refus.png',
  '✓': '/sprites/iconchat_valide.png',
  '✔': '/sprites/iconchat_valide.png',
  '✗': '/sprites/iconchat_refus.png',
  '🐟': '/sprites/ressource_fruit.png',
  '🚩': '/sprites/neutre_flag.png',
  '🎲': '/sprites/iconchat_interrogation.png',
  '🔧': '/sprites/arme_warshooter_bois.png',
  /* ── New iconchat-based mappings (kingdom cat heads & soldiers) ── */
  '✨': '/sprites/iconchat_valide.png',     /* shinies sparkle */
  '😾': '/sprites/iconchat_choquer.png',    /* angry/insult face */
  '🪡': '/sprites/iconchat_lancier.png',    /* needle/arsenal → soldier cat */
  '🏃': '/sprites/iconchat_lancier.png',    /* runner → lancier soldier */
};

/** Cached regex matching any known emoji in the catalog above. */
const EMOJI_REGEX = (() => {
  const keys = Object.keys(EMOJI_SPRITES).sort((a, b) => b.length - a.length);
  const escaped = keys.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  return new RegExp(escaped, 'g');
})();

/** Inline pixel-art icon `<img>` sized to the surrounding text height. */
export function inlineIcon(src: string): HTMLImageElement {
  const img = document.createElement('img');
  img.src = src;
  img.className = 'inline-icon';
  img.alt = '';
  img.decoding = 'async';
  return img;
}

/** True if `text` contains at least one mapped emoji. Cheap fast-path. */
export function hasEmoji(text: string): boolean {
  EMOJI_REGEX.lastIndex = 0;
  return EMOJI_REGEX.test(text);
}

/**
 * Replace an element's content with a label that may contain emoji-as-sprites.
 * Drop-in for `element.textContent = 'string with emoji'`.
 */
export function setIconText(target: HTMLElement, text: string): void {
  target.innerHTML = '';
  target.appendChild(renderTextWithIcons(text));
}

/**
 * Convert a string with emoji into a DocumentFragment where each known
 * emoji is swapped for a small pixel-art sprite.
 */
export function renderTextWithIcons(text: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  EMOJI_REGEX.lastIndex = 0;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = EMOJI_REGEX.exec(text)) !== null) {
    if (m.index > lastIndex) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
    }
    const url = EMOJI_SPRITES[m[0]];
    if (url) frag.appendChild(inlineIcon(url));
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    frag.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
  return frag;
}
